import React, { useState } from 'react';
import { ChevronDown, Shield, Lock, AlertTriangle, Info, ExternalLink, X } from 'lucide-react';

/**
 * Accordion Item Component
 */
const AccordionItem = ({ title, children, isOpen, onToggle, icon: Icon }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 bg-white hover:bg-gray-50 flex items-center justify-between text-left transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />}
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-700 space-y-2">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Security FAQ Page Component (Full Page View)
 * Addresses common user concerns about wallet authentication
 */
const SecurityFAQPage = ({ onClose }) => {
  const [openItems, setOpenItems] = useState([0]); // First item open by default

  const toggleItem = (index) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqs = [
    {
      icon: Shield,
      title: "Can this drain my wallet or steal my funds?",
      content: (
        <>
          <p className="font-semibold">No, absolutely not.</p>
          <p>
            Signature requests are <strong>read-only</strong>. They cannot move funds, approve tokens,
            or execute any blockchain transactions. Only transactions can do that, and we never ask
            you to approve transactions for login.
          </p>
          <div className="bg-blue-50 p-3 rounded mt-2">
            <p className="text-xs">
              <strong>Think of it like this:</strong> Signing is like showing your ID to prove who you are.
              It doesn't give away your money or keys.
            </p>
          </div>
        </>
      )
    },
    {
      icon: Lock,
      title: "What exactly am I signing?",
      content: (
        <>
          <p>
            You're signing a message that proves you control a specific wallet address.
            The message contains:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Website domain (e.g., coincommons.io)</li>
            <li>Your wallet address</li>
            <li>A statement (e.g., "Sign in to CoinCommons")</li>
            <li>A unique nonce (random code to prevent replay attacks)</li>
            <li>Timestamp of when the message was created</li>
          </ul>
          <p className="mt-2">
            This follows the <strong>Sign-In with Ethereum (SIWE)</strong> standard (EIP-4361),
            which is used across the Web3 ecosystem.
          </p>
        </>
      )
    },
    {
      icon: AlertTriangle,
      title: "How do I know this isn't a phishing attack?",
      content: (
        <>
          <p className="font-semibold mb-2">Check these signs of a legitimate signature request:</p>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <div>
                <strong>Domain matches:</strong> The domain in your wallet popup should match
                the website URL ({window.location.hostname})
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <div>
                <strong>Message is clear:</strong> You can read and understand what you're signing
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <div>
                <strong>No transaction value:</strong> There's no ETH amount or token approval shown
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <div>
                <strong>No token approvals:</strong> We never ask for permission to spend your tokens
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 p-3 rounded mt-3">
            <p className="text-xs">
              <strong>⚠️ Red flags to watch for:</strong> Mismatched domains, requests for token approvals,
              transaction confirmations with ETH amounts, or urgent/threatening language.
            </p>
          </div>
        </>
      )
    },
    {
      icon: Info,
      title: "Who else uses this authentication method?",
      content: (
        <>
          <p>This is the standard authentication method for Web3 applications. Major platforms using it include:</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-white p-2 rounded border border-gray-200">
              <strong>OpenSea</strong> - NFT marketplace
            </div>
            <div className="bg-white p-2 rounded border border-gray-200">
              <strong>Uniswap</strong> - DEX
            </div>
            <div className="bg-white p-2 rounded border border-gray-200">
              <strong>ENS</strong> - Domain names
            </div>
            <div className="bg-white p-2 rounded border border-gray-200">
              <strong>Lens Protocol</strong> - Social
            </div>
            <div className="bg-white p-2 rounded border border-gray-200">
              <strong>Farcaster</strong> - Social
            </div>
            <div className="bg-white p-2 rounded border border-gray-200">
              <strong>Mirror</strong> - Publishing
            </div>
          </div>
          <p className="mt-3">
            This standard (EIP-4361) was developed by leading Ethereum developers and security experts
            to provide a safe, consistent way to authenticate with Web3 apps.
          </p>
        </>
      )
    },
    {
      icon: Shield,
      title: "Is there any cost or gas fee?",
      content: (
        <>
          <p className="font-semibold">No, signing messages is completely free.</p>
          <p className="mt-2">
            Unlike blockchain transactions, signature requests:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Don't cost any gas fees</li>
            <li>Don't get recorded on the blockchain</li>
            <li>Happen instantly (no waiting for block confirmations)</li>
            <li>Can be done offline (no network connection needed)</li>
          </ul>
          <p className="mt-2">
            You only pay gas fees when you make actual blockchain transactions like sending tokens
            or interacting with smart contracts.
          </p>
        </>
      )
    },
    {
      icon: Lock,
      title: "What data does CoinCommons collect?",
      content: (
        <>
          <p>We collect minimal data to provide the service:</p>
          <div className="bg-white p-3 rounded border border-gray-200 mt-2 space-y-2">
            <div>
              <strong>✓ Your wallet address</strong> - To identify you and associate comments
            </div>
            <div>
              <strong>✓ Comments you write</strong> - Stored in our database
            </div>
            <div>
              <strong>✓ Addresses you watch</strong> - For notifications
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded mt-2">
            <p className="text-xs">
              <strong>We do NOT collect:</strong>
            </p>
            <ul className="text-xs list-disc pl-5 mt-1">
              <li>Your private keys (impossible to access)</li>
              <li>Your email or personal information (unless you provide it)</li>
              <li>Your transaction history</li>
              <li>Your token balances</li>
            </ul>
          </div>
        </>
      )
    },
    {
      icon: Info,
      title: "Can I disconnect my wallet anytime?",
      content: (
        <>
          <p>Yes, absolutely. You have complete control over your connection:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Disconnect from CoinCommons:</strong> Click your address in the top-right
              and select "Disconnect Wallet"
            </li>
            <li>
              <strong>Revoke from your wallet:</strong> Open MetaMask/WalletConnect, go to
              Settings → Connected Sites, and remove CoinCommons
            </li>
          </ul>
          <p className="mt-2">
            After disconnecting, you can still browse addresses and read comments, but you won't
            be able to post comments or vote until you reconnect.
          </p>
        </>
      )
    },
    {
      icon: ExternalLink,
      title: "Where can I learn more about Web3 security?",
      content: (
        <>
          <p>Here are some trusted resources to learn about Web3 security:</p>
          <div className="space-y-2 mt-3">
            <a
              href="https://eips.ethereum.org/EIPS/eip-4361"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="w-4 h-4" />
              EIP-4361: Sign-In with Ethereum Specification
            </a>
            <a
              href="https://metamask.io/security/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="w-4 h-4" />
              MetaMask Security Tips
            </a>
            <a
              href="https://support.metamask.io/hc/en-us/articles/4405506066331"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="w-4 h-4" />
              Understanding Signature Requests
            </a>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with close button */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Security FAQ</h1>
              <p className="text-sm text-gray-600">Everything you need to know about wallet sign-in</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              title={faq.title}
              icon={faq.icon}
              isOpen={openItems.includes(index)}
              onToggle={() => toggleItem(index)}
            >
              {faq.content}
            </AccordionItem>
          ))}
        </div>

        {/* Footer section */}
        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start gap-3 text-sm text-gray-600">
            <Info className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-600" />
            <div>
              <p className="font-semibold text-gray-900 mb-2">Still have questions?</p>
              <p>
                Feel free to reach out through our{' '}
                <a href="mailto:support@coincommons.io" className="text-blue-600 hover:text-blue-700 underline">
                  support email
                </a>
                {' '}or check our documentation for more information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityFAQPage;
